import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { X, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const SearchResultModal = ({ isOpen, onClose, result }) => {
  // حالة عدم وجود نتيجة
  if (!result && isOpen) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          dir="rtl"
          aria-describedby="search-result-desc"
          className="
            w-[95vw] max-w-2xl
            max-h-[85vh] overflow-y-auto
            rounded-2xl
            bg-gradient-to-br from-white to-red-50 border-2 border-red-200
            p-4 sm:p-6
          "
        >
          <DialogHeader>
            <DialogTitle className="text-center text-xl sm:text-3xl font-bold text-red-600 flex items-center justify-center gap-3">
              <AlertCircle className="w-7 h-7 sm:w-10 sm:h-10" />
              لم يتم العثور على نتائج
            </DialogTitle>
            <p id="search-result-desc" className="sr-only">
              لا توجد معاملة بهذا الرقم
            </p>
          </DialogHeader>

          <div className="text-center py-6 sm:py-12">
            <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <X className="w-8 h-8 sm:w-12 sm:h-12 text-red-500" />
            </div>

            <p className="text-lg sm:text-xl text-gray-700 mb-3 sm:mb-4 font-semibold">
              عذراً، لم يتم العثور على معاملة بهذا الرقم
            </p>
            <p className="text-sm sm:text-base text-gray-600">
              يرجى التأكد من رقم الجواز والمحاولة مرة أخرى
            </p>

            <div className="mt-5 sm:mt-6">
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                إغلاق
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!result) return null;

  const isDone = Boolean(result.deliveryDate);

  const getStatusIcon = () =>
    isDone ? (
      <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-green-500" />
    ) : (
      <Clock className="w-7 h-7 sm:w-8 sm:h-8 text-orange-500" />
    );

  const getStatusColor = () => (isDone ? 'from-green-50 to-green-100' : 'from-orange-50 to-orange-100');

  // عنصر صف واحد Responsive (عنوان + قيمة)
  const Row = ({ label, value, bg, borderColor = 'border-gray-300', valueClass = '' }) => (
    <div
      className={`
        p-4 sm:p-5 rounded-xl
        border-l-4 ${borderColor}
        ${bg}
      `}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-6">
        <div className="text-gray-700 text-right font-semibold text-sm sm:text-lg">{label}</div>
        <div className={`font-bold text-right text-gray-900 text-base sm:text-lg break-words ${valueClass}`}>
          {value || '-'}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        dir="rtl"
        aria-describedby="search-result-desc"
        className="
          w-[95vw] max-w-3xl
          max-h-[85vh] overflow-y-auto
          rounded-2xl
          bg-gradient-to-br from-white to-gray-50 border-2 border-green-200 shadow-2xl
          p-4 sm:p-6
        "
      >
        <DialogHeader>
          <DialogTitle className="text-center font-bold mb-3 sm:mb-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-4">
              {getStatusIcon()}
              <span className="text-2xl sm:text-3xl bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
                تفاصيل المعاملة
              </span>
            </div>
          </DialogTitle>

          <p id="search-result-desc" className="sr-only">
            تفاصيل حالة المعاملة حسب رقم الجواز
          </p>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4">
          <Row
            label="الإسم:"
            value={result.name}
            bg="bg-gradient-to-r from-blue-50 to-blue-100"
            borderColor="border-blue-500"
          />

          <Row
            label="رقم الجواز:"
            value={result.passportNumber}
            bg="bg-gradient-to-r from-purple-50 to-purple-100"
            borderColor="border-purple-500"
            valueClass="text-lg sm:text-xl"
          />

          <Row
            label="إسم المكتب:"
            value={result.office}
            bg="bg-gradient-to-r from-green-50 to-green-100"
            borderColor="border-green-500"
          />

          <Row label="تاريخ الإستلام:" value={result.receiveDate} bg="bg-white" />
          <Row label="تاريخ الإرسال للسفارة:" value={result.sendToEmbassyDate} bg="bg-white" />
          <Row label="تاريخ الخروج من السفارة:" value={result.exitFromEmbassyDate} bg="bg-white" />
          <Row label="تاريخ التسليم:" value={result.deliveryDate} bg="bg-white" />

          <div
            className={`
              p-4 sm:p-6 rounded-xl
              bg-gradient-to-r ${getStatusColor()}
              border-l-4 ${isDone ? 'border-green-500' : 'border-orange-500'}
            `}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-6">
              <div className="text-gray-700 text-right font-semibold text-sm sm:text-lg">الحالة:</div>
              <div className={`font-bold text-right text-base sm:text-lg ${isDone ? 'text-green-700' : 'text-orange-700'}`}>
                {result.status || '-'}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 sm:mt-6 text-center">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            إغلاق
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchResultModal;
